import { computed, ref } from 'vue'
import { createInjectionState, toReactive } from '@vueuse/core'
import { useOrderByFilter, usePagination } from '@/core'
import type { TBundle, TCoilPayload } from '../../../types'
import { useModuleQuery } from '../query/useModuleQuery'
import { Pagination } from '@/core/types'
import { useCatalogStore } from '@/core/store/catalogs'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { useOrderQuery } from '../query/useOrderQuery'
import { useCoilsStore } from '../../store/coils'

const [useProvideOrderModule, useOrderModule] = createInjectionState(() => {
  const rows = ref<number[]>([])
  const form = ref<TBundle>({})
  const bundles = ref<TBundle[]>([])
  const store = useCatalogStore()
  const { catalogs } = storeToRefs(store)
  const route = useRoute()
  const coilsStore = useCoilsStore()
  const { selectedCoils } = storeToRefs(coilsStore)
  const paginationModule = usePagination()

  const moduleQuery = useModuleQuery({
    modelId: computed(() => route.params?.id?.toString()),

    enableQuery: {
      loadRecords: false,
      loadBundlesFabricated: false,
      loadUsedCoils: false,
      loadRecord: computed(() => !!route.params?.id?.toString()),
      loadCoils: computed(() => !!route.params?.id?.toString()),
    },
  })

  const moduleOrderQuery = useOrderQuery({
    modelId: computed(() => route.params?.id?.toString()),
    currentPage: paginationModule.currentPage,
    enableQuery: {
      loadRecords: computed(() => !!route.params?.id?.toString()),
    },
    handlers: {
      onDataChange: (pagination: Pagination) => {
        paginationModule.setPagination(pagination)
      },
    },
  })

  const { orderByOptions } = useOrderByFilter({
    columns: ['created_at'],
  })

  const onAdd = async (payload: TBundle) => {
    await moduleOrderQuery.addRecord.mutateAsync({
      ...payload,
      project_id: Number(route.params?.id?.toString()),
    })
    await moduleOrderQuery.loadRecords.refetch()
    moduleQuery.loadRecord.refetch()
  }

  const onEdit = async (payload: TBundle) => {
    await moduleOrderQuery.editRecord.mutateAsync(payload)
    await moduleOrderQuery.loadRecords.refetch()
    moduleQuery.loadRecord.refetch()
  }

  const onDelete = async (payload: TBundle) => {
    await moduleOrderQuery.deleteRecord.mutateAsync(payload)
    await moduleOrderQuery.loadRecords.refetch()
    moduleQuery.loadRecord.refetch()
  }

  const onAddCoils = async (payload: TCoilPayload) => {
    await moduleOrderQuery.addCoils.mutateAsync({
      ...payload,
    })
    moduleQuery.loadRecord.refetch()
  }

  const onPrintRecords = async (id: number, type: string) => {
    await moduleOrderQuery.printRecord(id, type)
  }

  const tableData = computed(() => moduleOrderQuery.loadRecords.data?.value)
  const model = computed(() => moduleQuery.loadRecord?.data?.value)
  const isLoading = computed(
    () =>
      moduleOrderQuery.addRecord.isLoading.value ||
      moduleOrderQuery.editRecord.isLoading.value ||
      moduleOrderQuery.loadRecords.isLoading.value,
  )
  const isLoadingCoils = computed(
    () => moduleOrderQuery.addCoils.isLoading.value,
  )

  return {
    tableData,
    orderByOptions,
    rows,
    form,
    model,
    isLoading,
    catalogs,
    bundles,
    modulePagination: toReactive(paginationModule),
    selectedCoils,
    isLoadingCoils,
    onAddCoils,
    onDelete,
    onEdit,
    onAdd,
    onPrintRecords,
  }
})

export { useProvideOrderModule, useOrderModule }
